import React, { useEffect } from 'react';
import styled from 'styled-components';
import { LayoutColors } from '@src/core/theme';
import { Scheduling } from '@src/core/buyerApiClient';
import { useAppDispatch, useAppSelector, useTranslate } from '@src/core/hooks';
import {
  schedulingSeatMapSelector,
  loadSeatMap,
  selectedProductTariffHour,
  selectedTariffsSelector,
  setSeatMapAllocationStatus,
  seatMapAllocationStatusSelector,
  schedulingSeatMapLoadingSelector,
} from '@src/features/product';
import { useWidgetId } from '@src/core/IdProvider';
import { makeSelectedTariffs } from '@src/common/tariffs';
import { getSeatMapBestAllocationStatus } from '@src/common/seatMaps';
import Loading from '../ui2/Loading';

interface Props {
  scheduling: Scheduling;
  timeMinutes?: number;
}

const SeatsUnavailableAlert = (props: Props) => {
  const { scheduling, timeMinutes } = props;
  const { i18nElement } = useTranslate();

  const widgetId = useWidgetId();
  const dispatch = useAppDispatch();
  const seatMap = useAppSelector(schedulingSeatMapSelector(scheduling.id, timeMinutes));
  const tariffsPerTypeSelected = useAppSelector(selectedTariffsSelector(widgetId));
  const selectedHour = useAppSelector(selectedProductTariffHour(widgetId));
  const selectedTariffs = makeSelectedTariffs(tariffsPerTypeSelected, selectedHour?.minutes ?? null);
  const allocationStatus = useAppSelector(seatMapAllocationStatusSelector(widgetId));
  const isSeatMapLoading = useAppSelector(schedulingSeatMapLoadingSelector(scheduling.id));
  const tariffQuantity = selectedTariffs.reduce((acc, tariff) => acc + tariff.quantity, 0);

  useEffect(() => {
    if (scheduling?.requiresSeatSelection && timeMinutes) {
      dispatch(loadSeatMap(scheduling, timeMinutes));
    }
  }, [scheduling, timeMinutes]);

  useEffect(() => {
    let allocationStatus = { success: true, missingCapacity: 0, missingTariffs: 0 };

    if (seatMap && selectedTariffs.length > 0) {
      allocationStatus = getSeatMapBestAllocationStatus(seatMap, selectedTariffs);
    }

    dispatch(setSeatMapAllocationStatus(widgetId, allocationStatus));
  }, [tariffQuantity, seatMap?.id]);

  if (isSeatMapLoading) {
    return <Loading size={40} color={'#262ba6'} />;
  }

  if (allocationStatus.success) {
    return null;
  }

  return (
    <AlertContainer>
      <p>
        {allocationStatus.missingTariffs > 0
          ? i18nElement('adder_seats_missing_tariffs_unavailable_error', {
              missing: String(allocationStatus.missingTariffs),
            })
          : i18nElement('adder_seats_unavailable_error')}
      </p>
    </AlertContainer>
  );
};

const AlertContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  padding: 16px;
  font-weight: bold;
  border-radius: 4px;
  background-color: ${LayoutColors.errorLight};

  p {
    color: ${LayoutColors.errorDark};
  }
`;

export default SeatsUnavailableAlert;
